import React, { useState } from 'react';
import { Lock, Calendar, User, ArrowRight, BookOpen, MessageSquare, FileText, Share2, Image as ImageIcon, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const WallOfStories = () => {
  const [activeTab, setActiveTab] = useState('blog');
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  const blogPosts = [
    {
      id: 1,
      title: "Exploring Haunted Places in Malaysia: Culture, Memory, and the Stories We Keep",
      author: "Natasha Razak",
      date: "2024-12-10",
      category: "Field Notes",
      excerpt: "Across Malaysia, places labeled as 'haunted' are more than eerie landmarks; they are windows into the country's layered history, collective memory, and cultural imagination. While ghost stories and supernatural beliefs exist in every society, they often hold a more prominent place in Southeast Asia...",
      images: [
        "https://customer-assets.emergentagent.com/job_dark-heritage-my/artifacts/wfg5ykip_Exploring%20haunted%20places%20-%20Kellie%27s%20Castle.jpg",
        "https://customer-assets.emergentagent.com/job_dark-heritage-my/artifacts/noawyfd3_Exploring%20haunted%20places%20-%20Villa%20Nabila.JPG"
      ],
      isPremium: true,
      fullContent: `Culture, Memory, and the Stories We Keep

Across Malaysia, places labeled as "haunted" are more than eerie landmarks; they are windows into the country's layered history, collective memory, and cultural imagination. While ghost stories and supernatural beliefs exist in every society, they often hold a more prominent place in Southeast Asia, where centuries of Malay folklore, colonial encounters, and diverse spiritual traditions intersect.

In Malaysia, the concept of "haunted" carries meanings that stretch beyond fear. It often evokes respect for unseen worlds, curiosity about the unknown, and an instinctive connection to stories passed down through generations.

Why Malaysians Are Drawn to Haunted Places

The bond people feel toward these locations is rarely just about thrill-seeking or the pursuit of adrenaline. For many Malaysians, on the other hand, the fascination reflects a blend of cultural upbringing and shared social experience:

• Respect for the supernatural: Within Malay, Chinese, Indian, and Indigenous belief systems, the spirit world coexists with daily life.
• Thrill-seeking and facing childhood stories: Many Malaysians grow up hearing legends linked to abandoned schools, old mansions, or forgotten hillsides.
• Connecting with heritage: Some places that Malaysians refer to as "haunted sites" are, in fact, deeply historical.
• Shared storytelling: Ghost tales foster bonding.

Notable Haunted Sites in Malaysia

Malaysia is home to many locations wrapped in folklore, history, and local legend. Among the most talked-about are:

• Kellie's Castle, Perak: An unfinished colonial mansion built by Scottish planter William Kellie Smith
• 99-Door Mansion, Penang: Once a grand plantation residence
• Villa Nabila, Johor: A decaying hilltop bungalow with tales from the 1970s
• Karak Highway, Pahang: Known for both accidents and spectral tales
• Ria Apartment, Genting Highlands: An abandoned building associated with mysterious events`
    },
    {
      id: 2,
      title: "The Stories Behind Malaysia's Hidden Shadows",
      author: "Natasha Razak",
      date: "2024-11-28",
      category: "Historical Research",
      excerpt: "Many places in Malaysia appear ordinary at first glance, yet carry stories that shaped local memory in ways visitors rarely realize. The best experience comes from understanding these hidden layers. From Lalaport Shopping Mall built on Pudu Jail grounds to the Parit Sulong Bridge...",
      images: [
        "https://customer-assets.emergentagent.com/job_dark-heritage-my/artifacts/ezzefw3d_The%20stories%20behind...%20Old%20court%20house%20Kuching.jpeg",
        "https://customer-assets.emergentagent.com/job_dark-heritage-my/artifacts/h6lp4956_The%20stories%20behind...%20Parit%20Sulong.jpeg"
      ],
      isPremium: true,
      fullContent: `Many places in Malaysia appear ordinary at first glance, yet carry stories that shaped local memory in ways visitors rarely realize. The best experience comes from understanding these hidden layers.

Lalaport Shopping Mall: A Modern Space Built on a Heavy Past

While you are peacefully strolling through Lalaport, it may never cross your mind that this bright and modern complex stands on the former grounds of Pudu Jail. For many Malaysians, the past is harder to ignore.

Old Courthouse, Kuching: A Beautiful Landmark With a Shadowed Past

Today, the Old Courthouse in central Kuching looks tranquil and inviting. Its whitewashed colonial buildings surround a pleasant courtyard filled with greenery, a gentle fountain and a selection of cafés, galleries and restaurants.

Parit Sulong Bridge: Quiet River, Heavy History

Parit Sulong looks like a typical small Malaysian town at first glance. Yet this gentle town holds one of the most painful memories of the Second World War in Malaya.

Sulug Island: Tropical Paradise with Heavy Memories

Sulug Island rests just off the coast of Kota Kinabalu, a small piece of land with bright sand, clear water and a soft stillness that feels almost untouched.`
    }
  ];

  return (
    <div className="min-h-screen bg-[#1a1c1b] pt-20">
      {/* Hero */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/fort-margarita-kuching.jpg"
            alt="Hidden Stories"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#1a1c1b]"></div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="container-custom pb-20 md:pb-32">
            <div className="max-w-4xl">
              <h1 className="text-white font-light text-5xl md:text-7xl mb-6 leading-tight">
                Hidden Stories
              </h1>
              <p className="text-lg md:text-xl font-light text-white/90 mb-8 leading-relaxed max-w-2xl">
                Field notes, research insights, and personal reflections from Malaysia's dark tourism landscape.
              </p>
              <button 
                onClick={() => setShowSubscribeModal(true)}
                className="btn-primary"
              >
                Subscribe for RM5/month
              </button>
            </div>
          </div>
        </div>

        {/* Decorative Ground Element */}
        <div className="absolute bottom-0 left-0 right-0 h-32 md:h-48">
          <img 
            src="/images/villa-nabila-johor-2.jpg" 
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
        </div>
      </section>

      {/* Sub Navigation */}
      <section className="bg-[#0f0f10] border-b border-[#3a2222] sticky top-20 z-40">
        <div className="container-custom">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('blog')}
              className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'blog' 
                  ? 'text-[#dc2626] border-b-2 border-[#dc2626]' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <BookOpen size={18} />
              Blog (Paid / Subscribed)
            </button>
            <button
              onClick={() => setActiveTab('ethical')}
              className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'ethical' 
                  ? 'text-[#dc2626] border-b-2 border-[#dc2626]' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <MessageSquare size={18} />
              Ethical Debates
            </button>
            <button
              onClick={() => setActiveTab('references')}
              className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'references' 
                  ? 'text-[#dc2626] border-b-2 border-[#dc2626]' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <FileText size={18} />
              References & Reads
            </button>
            <button
              onClick={() => setActiveTab('share')}
              className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'share' 
                  ? 'text-[#dc2626] border-b-2 border-[#dc2626]' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <Share2 size={18} />
              Share Your Story
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'gallery' 
                  ? 'text-[#dc2626] border-b-2 border-[#dc2626]' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <ImageIcon size={18} />
              Gallery
            </button>
          </div>
        </div>
      </section>

      {/* Blog Tab */}
      {activeTab === 'blog' && (
        <section className="space-section bg-[#0f0f10]">
          <div className="container-custom">
            <div className="max-w-5xl">
              <div className="space-y-12">
                {blogPosts.map((post) => (
                  <article 
                    key={post.id}
                    className="bg-[#1a1c1b] rounded-lg overflow-hidden border border-[#3a2222] hover:border-[#dc2626]/50 transition-all"
                  >
                    {/* Images Grid */}
                    <div className="grid md:grid-cols-2 gap-0">
                      {post.images.map((img, idx) => (
                        <div key={idx} className="relative h-64">
                          <img 
                            src={img}
                            alt={post.title}
                            className="w-full h-full object-cover"
                          />
                          {post.isPremium && idx === 0 && (
                            <div className="absolute top-4 right-4 bg-[#dc2626] text-white px-3 py-1 rounded-full flex items-center gap-2">
                              <Lock size={14} />
                              <span className="text-xs font-bold uppercase">Premium</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Content */}
                    <div className="p-8">
                      <div className="flex items-center gap-4 mb-4 text-[#888680]">
                        <span className="caption text-[#dc2626]">{post.category}</span>
                        <span className="flex items-center gap-2">
                          <Calendar size={14} />
                          <span className="caption">{post.date}</span>
                        </span>
                        <span className="flex items-center gap-2">
                          <User size={14} />
                          <span className="caption">{post.author}</span>
                        </span>
                      </div>

                      <h2 className="heading-3 text-white mb-4">{post.title}</h2>
                      
                      {/* Show only first 2 lines with fade overlay */}
                      <div className="relative">
                        <p className="body-medium text-white/70 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#1a1c1b] to-transparent"></div>
                      </div>

                      <div className="mt-6 pt-4 border-t border-[#3a2222]">
                        <button 
                          onClick={() => setShowSubscribeModal(true)}
                          className="btn-secondary text-sm flex items-center gap-2"
                          data-testid={`subscribe-btn-${post.id}`}
                        >
                          <Lock size={16} />
                          Subscribe to Read Full Story
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Ethical Debates Tab */}
      {activeTab === 'ethical' && (
        <section className="space-section bg-[#0f0f10]">
          <div className="container-custom">
            <div className="max-w-4xl">
              <h2 className="heading-2 text-[#dc2626] mb-8">Ethical Debates in Dark Tourism</h2>
              <p className="body-large text-white/80 mb-12">
                Coming soon: Discussions on the ethical considerations of visiting sites of tragedy,
                commercialization concerns, and respectful tourism practices.
              </p>
              <div className="bg-[#302f2c] p-8 rounded-lg border border-[#3a2222]">
                <p className="body-medium text-white/70">
                  This section will feature thoughtful debates on sensitive topics including authenticity vs. exploitation,
                  local vs. visitor perspectives, and the balance between education and entertainment.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* References & Reads Tab */}
      {activeTab === 'references' && (
        <section className="space-section bg-[#0f0f10]">
          <div className="container-custom">
            <div className="max-w-4xl">
              <h2 className="heading-2 text-[#dc2626] mb-8">References & Recommended Reads</h2>
              <p className="body-large text-white/80 mb-12">
                Coming soon: Curated academic papers, books, and articles on dark tourism, 
                Malaysian history, and heritage preservation.
              </p>
              <div className="bg-[#302f2c] p-8 rounded-lg border border-[#3a2222]">
                <p className="body-medium text-white/70">
                  We're compiling a comprehensive bibliography including works by Stone (2006), 
                  Sharpley (2009), and local Malaysian historians.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Share Your Story Tab */}
      {activeTab === 'share' && (
        <section className="space-section bg-[#0f0f10]">
          <div className="container-custom">
            <div className="max-w-4xl">
              <h2 className="heading-2 text-[#dc2626] mb-8">Share Your Story</h2>
              <p className="body-large text-white/80 mb-12">
                Have you visited a dark tourism site in Malaysia? Share your experience, 
                memories, or knowledge to help preserve our heritage.
              </p>
              <Link to="/share-story" className="btn-primary">
                Submit Your Story <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <section className="space-section bg-[#0f0f10]">
          <div className="container-custom">
            <div className="max-w-6xl">
              <h2 className="heading-2 text-[#dc2626] mb-8">Photo Gallery</h2>
              <p className="body-large text-white/80 mb-12">
                Coming soon: A visual archive of Malaysia's dark tourism sites, 
                documenting their current state and historical significance.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-[#302f2c] h-64 rounded-lg flex items-center justify-center border border-[#3a2222]">
                  <ImageIcon className="text-[#dc2626]" size={48} />
                </div>
                <div className="bg-[#302f2c] h-64 rounded-lg flex items-center justify-center border border-[#3a2222]">
                  <ImageIcon className="text-[#dc2626]" size={48} />
                </div>
                <div className="bg-[#302f2c] h-64 rounded-lg flex items-center justify-center border border-[#3a2222]">
                  <ImageIcon className="text-[#dc2626]" size={48} />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Subscribe Modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-[#1a1c1b] rounded-lg max-w-2xl w-full p-8 border border-[#3a2222] relative max-h-[90vh] overflow-y-auto">
            {/* Close / Back button */}
            <button 
              onClick={() => setShowSubscribeModal(false)}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
              data-testid="modal-close-btn"
            >
              <X size={24} />
            </button>

            <h2 className="heading-2 text-[#dc2626] mb-6">Subscribe to Wall of Stories</h2>
            
            <p className="body-medium text-white/80 mb-8">
              Get unlimited access to premium field notes, research insights, and exclusive stories 
              for just <strong className="text-[#dc2626]">RM5 per month</strong>.
            </p>

            <div className="space-y-6 mb-8">
              <h3 className="heading-4 text-white">Payment Options:</h3>
              
              {/* PayPal */}
              <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222]">
                <h4 className="text-white font-semibold mb-3">PayPal</h4>
                <p className="body-small text-white/70 mb-4">
                  Pay securely with your PayPal account or credit card.
                </p>
                <a 
                  href="https://www.paypal.com/paypalme/natasharazak/5MYR"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full text-center block"
                >
                  Subscribe via PayPal
                </a>
                <p className="body-small text-white/50 mt-3 text-center">
                  PayPal: natasha.razak@mail.com
                </p>
              </div>

              {/* Bank QR */}
              <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222]">
                <h4 className="text-white font-semibold mb-3">Touch 'n Go eWallet / Bank Transfer</h4>
                <p className="body-small text-white/70 mb-4">
                  Scan the QR code below with your banking app or eWallet to make payment, then email us your receipt.
                </p>
                <div className="flex justify-center">
                  <img 
                    src="/images/payment-qr.jpeg" 
                    alt="Touch 'n Go eWallet QR - Natasa Razak" 
                    className="w-64 rounded-lg"
                    data-testid="payment-qr-code"
                  />
                </div>
                <p className="body-small text-white/70 mt-4">
                  After payment, email receipt to: <strong className="text-[#dc2626]">contact@darktourism-malaysia.com</strong>
                </p>
              </div>
            </div>

            <button 
              onClick={() => setShowSubscribeModal(false)}
              className="btn-secondary w-full"
              data-testid="modal-back-btn"
            >
              Back to Page
            </button>
          </div>
        </div>
      )}

      {/* What You Get */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 text-[#dc2626] mb-8 text-center">What Subscribers Get</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222] text-center">
                <div className="w-16 h-16 bg-[#dc2626] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="text-white" size={32} />
                </div>
                <h3 className="heading-4 text-white mb-3">Exclusive Content</h3>
                <p className="body-small text-white/70">
                  Access premium field notes and in-depth research not available elsewhere.
                </p>
              </div>

              <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222] text-center">
                <div className="w-16 h-16 bg-[#dc2626] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="text-white" size={32} />
                </div>
                <h3 className="heading-4 text-white mb-3">Regular Updates</h3>
                <p className="body-small text-white/70">
                  New stories added monthly from ongoing field research across Malaysia.
                </p>
              </div>

              <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222] text-center">
                <div className="w-16 h-16 bg-[#dc2626] rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="text-white" size={32} />
                </div>
                <h3 className="heading-4 text-white mb-3">Support Research</h3>
                <p className="body-small text-white/70">
                  Your subscription directly supports preservation and documentation work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WallOfStories;
